import { AxiosResponse } from 'axios'
import apiClient from 'api/common/common'
import { CampaignPreRegisterRequest, CampaignQuestIdAuthenticateRequest, CampaignQuestIdIssueRequest } from 'api/common/requests'
import { CampaignQrInspectResponse, CampaignQuestIdResponse, CampaignStatusResponse } from 'api/common/responses'
import { Status } from 'api/common/statuses'

const STATUS_HEADER = 'x-aqc-status' as const
const FALLBACK_STATUS: Status = 'failed' as Status

const extractStatus = (response: AxiosResponse | undefined): Status => {
  return (response?.headers?.[STATUS_HEADER] as Status | undefined) ?? FALLBACK_STATUS
}

/**
 * キャンペーン QR ハッシュ検証 API (要件書 §4.1 / §4.14 — Rev.18 D7)
 *
 * QR トークンを BE 側で AES 復号し `{ isCampaign, isExpired, issuedAt }` を返す。
 * フロント単独 AES 復号は鍵管理リスク回避のため非採用。
 * 期限切れ時 (`campaignQrExpired`) も `response` は同形式で返るため、
 * フロントでは status と response の双方を参照可能。
 */
export const campaignQrInspectAPI = async (hash: string, callback: (status: Status, response: CampaignQrInspectResponse | null) => void): Promise<void> => {
  await apiClient
    .post('/campaign/qr/inspect', { hash })
    .then((response: AxiosResponse<CampaignQrInspectResponse>) => {
      callback(extractStatus(response), response?.data ?? null)
    })
    .catch((error) => {
      console.error(error)
      callback(FALLBACK_STATUS, null)
    })
}

/**
 * キャンペーン仮登録 API (要件書 §4.5 ステージA)
 *
 * 確認メール送信のみを実行。Connector / Position はステージB (verify) で生成される。
 */
export const campaignPreRegisterAPI = async (request: CampaignPreRegisterRequest, callback: (status: Status) => void): Promise<void> => {
  await apiClient
    .post('/campaign/preRegister', request)
    .then((response: AxiosResponse) => {
      callback(extractStatus(response))
    })
    .catch((error) => {
      console.error(error)
      callback(FALLBACK_STATUS)
    })
}

/**
 * キャンペーン確認メールトークン検証 API (要件書 §4.5 ステージB)
 *
 * BE 側でトランザクション (Connector + Position×3 + ApplyLog + BinaryMap + Referral) を実行。
 */
export const campaignVerifyTokenAPI = async (token: string, callback: (status: Status) => void): Promise<void> => {
  await apiClient
    .get(`/campaignSignUp/verify/${token}`)
    .then((response: AxiosResponse) => {
      callback(extractStatus(response))
    })
    .catch((error) => {
      console.error(error)
      callback(FALLBACK_STATUS)
    })
}

/**
 * キャンペーン参加状況ポーリング API (要件書 §7.5)
 *
 * Promise 直返し方式 (BpoStatus 既存パターンに整合、ポーリングフックから呼出)。
 */
export const getCampaignStatusAPI = async (token: string): Promise<{ status: Status; response: CampaignStatusResponse | null }> => {
  return apiClient
    .get(`/campaign/status/${token}`)
    .then((response: AxiosResponse<CampaignStatusResponse>) => ({
      status: extractStatus(response),
      response: response?.data ?? null,
    }))
    .catch((error) => {
      console.error(error)
      return { status: FALLBACK_STATUS, response: null }
    })
}

/**
 * Quest UID 認証 API (要件書 §3.2.1 パターン1 / Rev.16 D5 / Rev.17 S40)
 *
 * BE 経由で ELL_Portal Quest 認証 API に到達 (BE PR #334 / #335 で実装)。
 * 失敗時は Status.QuestAuthFailed (情報漏洩抑制で auth 失敗と password 不一致を統合) /
 * Status.QuestAccountDisabled (account_status != 1) / Status.InternalError のいずれかを返却。
 * Quest パスワードは callback 完了後に呼出側で即破棄すること (要件書 §3.2.1 line 376)。
 */
export const campaignQuestIdAuthenticateAPI = async (
  request: CampaignQuestIdAuthenticateRequest,
  callback: (status: Status, response: CampaignQuestIdResponse | null) => void,
): Promise<void> => {
  await apiClient
    .post('/campaign/questId/authenticate', request)
    .then((response: AxiosResponse<CampaignQuestIdResponse>) => {
      callback(extractStatus(response), response?.data ?? null)
    })
    .catch((error) => {
      console.error(error)
      callback(FALLBACK_STATUS, null)
    })
}

/**
 * Quest UID 自動発行 API (要件書 §3.2.1 パターン2 / Rev.17 S40-S41)
 *
 * BE 経由で ELL_Portal Quest ID 自動発行 API に到達 (BE PR #334 / #335 で実装)。
 * `name` / `password` は ELL_Portal 側 User 作成にも使用される (Rev.17 S41)。
 * 既存 email との冪等返却 (created=true/false) は BE の atemQuestId として透過伝搬。
 * 失敗時は Status.QuestAccountDisabled / Status.InternalError のいずれかを返却。
 */
export const campaignQuestIdIssueAPI = async (
  request: CampaignQuestIdIssueRequest,
  callback: (status: Status, response: CampaignQuestIdResponse | null) => void,
): Promise<void> => {
  await apiClient
    .post('/campaign/questId/issue', request)
    .then((response: AxiosResponse<CampaignQuestIdResponse>) => {
      callback(extractStatus(response), response?.data ?? null)
    })
    .catch((error) => {
      console.error(error)
      callback(FALLBACK_STATUS, null)
    })
}
